import { useState } from 'react';
import { toast } from 'sonner';
import { useUpdateBewerber } from '../../api/bewerber';
import {
  Bewerber,
  BewerberStatus,
  OnboardingStep,
  einstellenStepsFuer,
} from '../../types/bewerber';
import { StichtagInput } from './StichtagInput';

interface OnboardingChecklistProps {
  bewerber: Bewerber;
  /**
   * Fold the list away behind its header, expandable on click. Used where the
   * Einstellen-Nachweis is context rather than the task at hand — on an
   * OFFBOARDING Bewerber it sits under the offboarding list.
   */
  collapsible?: boolean;
}

export function OnboardingChecklist({ bewerber, collapsible = false }: OnboardingChecklistProps) {
  const updateMutation = useUpdateBewerber();
  const [pendingField, setPendingField] = useState<OnboardingStep['field'] | null>(null);
  const [datePending, setDatePending] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // In GEWONNEN wird die Liste weitergefuehrt, nicht nur gelesen -- wer schon
  // arbeitet, kann durchaus noch einen Schritt nachtragen. Erst der Abgang
  // friert sie ein: ab OFFBOARDING ist sie Nachweis, nicht Aufgabe.
  const readOnly = bewerber.status === BewerberStatus.OFFBOARDING;
  const steps = einstellenStepsFuer(bewerber.status);
  const doneCount = steps.filter((step) => bewerber[step.field]).length;
  const total = steps.length;

  const handleToggle = async (step: OnboardingStep, checked: boolean) => {
    setPendingField(step.field);
    try {
      await updateMutation.mutateAsync({
        id: bewerber.id,
        data: { [step.field]: checked },
      });
      toast.success(`„${step.label}" ${checked ? 'erledigt' : 'zurückgesetzt'}`);
    } catch (error) {
      console.error('Onboarding step update failed:', error);
      toast.error('Schritt konnte nicht gespeichert werden');
    } finally {
      setPendingField(null);
    }
  };

  // Documents only: the date moves no status. An empty field clears it.
  const handleErsterArbeitstag = async (value: string) => {
    setDatePending(true);
    try {
      await updateMutation.mutateAsync({
        id: bewerber.id,
        data: { onboardingErsterArbeitstag: value || null },
      });
      toast.success(value ? 'Erster Arbeitstag gespeichert' : 'Erster Arbeitstag entfernt');
    } catch (error) {
      console.error('First working day update failed:', error);
      toast.error('Erster Arbeitstag konnte nicht gespeichert werden');
    } finally {
      setDatePending(false);
    }
  };

  const isOpen = !collapsible || expanded;

  const header = (
    <>
      <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
        {collapsible && (
          <svg
            className={`h-4 w-4 mr-1.5 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
        Einstellen{readOnly ? ' (abgeschlossen)' : ''}
      </h3>
      <span className="text-sm font-medium text-gray-500">
        {doneCount} / {total} erledigt
      </span>
    </>
  );

  return (
    <div className="bg-white shadow rounded-lg">
      {collapsible ? (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={isOpen}
          className={`w-full px-4 py-5 sm:px-6 flex items-center justify-between text-left hover:bg-gray-50 rounded-t-lg ${
            isOpen ? 'border-b border-gray-200' : 'rounded-b-lg'
          }`}
        >
          {header}
        </button>
      ) : (
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex items-center justify-between">
          {header}
        </div>
      )}
      <div className={`px-4 py-5 sm:p-6 ${isOpen ? '' : 'hidden'}`}>
        <div className="mb-5 grid grid-cols-1 gap-4 border-b border-gray-200 pb-5 sm:grid-cols-2">
          <StichtagInput
            field="onboardingErsterArbeitstag"
            label="Erster Arbeitstag"
            saved={bewerber.onboardingErsterArbeitstag?.slice(0, 10) ?? ''}
            disabled={readOnly || datePending}
            onSave={handleErsterArbeitstag}
          />
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {steps.map((step) => {
            const checked = bewerber[step.field];
            const isPending = pendingField === step.field;
            return (
              <li key={step.field} className="flex items-start">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={readOnly || isPending}
                    onChange={(e) => handleToggle(step, e.target.checked)}
                    className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">{step.label}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default OnboardingChecklist;
